import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import { FAVORITE_SECTION_NAME } from '@repo/common';

/**
 * When a game is added to favorites in multi-view, scroll down to reveal the
 * Favorites section — but only if the user is already at the bottom of the lobby
 * (the section is in view). Keeps the behaviour consistent instead of the scroll
 * jumping only on some adds.
 *
 * @param containerRef   The scrollable multi-view lobby container.
 * @param favoritesCount The current number of games in the favorites section.
 */
export const useRevealFavoritesOnAdd = (
  containerRef: RefObject<HTMLElement | null>,
  favoritesCount: number,
): void => {
  const prevCountRef = useRef<number | null>(null);

  useEffect(() => {
    const prevCount = prevCountRef.current;
    prevCountRef.current = favoritesCount;

    // Only react to additions (skip the first render and removals).
    if (prevCount === null || favoritesCount <= prevCount) return;

    const container = containerRef.current;
    // Ignore a hidden container (single-view active → display: none).
    if (!container || container.offsetParent === null) return;

    // Reveal only if the Favorites section is already in view, i.e. the user is at
    // the bottom of the lobby. (Favorites grows downward, so its top stays put.)
    const favEl = container.querySelector(`[data-section="${FAVORITE_SECTION_NAME}"]`);
    if (!favEl || favEl.getBoundingClientRect().top > container.getBoundingClientRect().bottom) return;

    container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
  }, [containerRef, favoritesCount]);
};
